import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useToast } from '../../contexts/ToastContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowLeft, 
  MapPin, 
  Calendar, 
  Users, 
  Eye, 
  Briefcase, 
  Clock,
  Building,
  IndianRupee,
  Mail,
  Phone,
  Share2,
  AlertCircle,
  CheckCircle,
  User,
  Star,
  BookOpen,
  Award,
  ExternalLink
} from 'lucide-react';
import { jobAPI } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';

interface JobDetails {
  _id: string;
  jobId: string;
  title: string;
  companyName: string;
  location: string;
  employmentType: string;
  experienceLevel: string;
  salaryRange: {
    min: number;
    max: number;
    currency: string;
    period: string;
  };
  description: string;
  requiredSkills: string[];
  qualifications: string;
  jobCategory: string;
  numberOfOpenings: number;
  lastDateToApply: string;
  contactEmail: string;
  contactPhone: string;
  views: number;
  applications: number;
  createdAt: string;
  postedBy: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  status: string;
  isActive: boolean;
}

export default function JobDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  
  const [job, setJob] = useState<JobDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [applying, setApplying] = useState(false);
  const { success } = useToast();

  useEffect(() => {
    if (id) {
      fetchJobDetails();
    }
  }, [id]);

  const fetchJobDetails = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await jobAPI.getJobById(id!);
      
      if (response.success) {
        setJob(response.data);
      } else {
        setError('Job not found');
      }
    } catch (err: any) {
      console.error('Job details fetch error:', err);
      setError(err.response?.data?.message || 'Failed to fetch job details');
    } finally {
      setLoading(false);
    }
  };

  const handleApply = () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    if (job) {
      // Open email client with pre-filled subject and body
      const subject = `Application for ${job.title} - ${job.jobId}`;
      const body = `Dear Hiring Manager,

I am writing to express my interest in the ${job.title} position at ${job.companyName}.

Please find my resume attached. I would be happy to discuss how my skills and experience align with your requirements.

Thank you for your consideration.

Best regards,
${user?.firstName} ${user?.lastName}`;
      
      const mailtoLink = `mailto:${job.contactEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      window.location.href = mailtoLink;
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: job?.title,
          text: `Check out this job opportunity: ${job?.title} at ${job?.companyName}`,
          url: window.location.href
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      // Fallback - copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      success('Job link copied to clipboard!');
    }
  };

  const formatSalary = (salaryRange: JobDetails['salaryRange']) => {
    if (!salaryRange || (!salaryRange.min && !salaryRange.max)) {
      return 'Not disclosed';
    }
    
    const { min, max, currency = '₹', period = 'per month' } = salaryRange;
    
    // If both min and max are missing or invalid
    if (!min && !max) {
      return 'Not disclosed';
    }
    
    // If only one value is provided
    if (!min && max) {
      return `Up to ${currency} ${max.toLocaleString()} ${period}`;
    }
    if (min && !max) {
      return `From ${currency} ${min.toLocaleString()} ${period}`;
    }
    
    // If both values are the same
    if (min === max) {
      return `${currency} ${min.toLocaleString()} ${period}`;
    }
    
    // If both values are different
    return `${currency} ${min.toLocaleString()} - ${max.toLocaleString()} ${period}`;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) {
      return 'Not specified';
    }
    
    const date = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return 'Invalid Date';
    }
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getTimeRemaining = (dateString: string) => {
    if (!dateString) {
      return { text: 'No deadline', color: 'text-gray-600', bg: 'bg-background' };
    }
    
    const date = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return { text: 'Invalid date', color: 'text-gray-600', bg: 'bg-background' };
    }
    
    const now = new Date();
    const diffTime = date.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return { text: 'Expired', color: 'text-red-600', bg: 'bg-red-50' };
    } else if (diffDays === 0) {
      return { text: 'Today', color: 'text-orange-600', bg: 'bg-orange-50' };
    } else if (diffDays === 1) {
      return { text: 'Tomorrow', color: 'text-orange-600', bg: 'bg-orange-50' };
    } else if (diffDays <= 7) {
      return { text: `${diffDays} days left`, color: 'text-yellow-600', bg: 'bg-yellow-50' };
    } else {
      return { text: `${diffDays} days left`, color: 'text-primary', bg: 'bg-green-50' };
    }
  };

  const getEmploymentTypeColor = (type: string) => {
    switch (type) {
      case 'Full-time':
        return 'bg-primary-light text-green-800';
      case 'Part-time':
        return 'bg-primary-light text-blue-800';
      case 'Contract':
        return 'bg-orange-100 text-orange-800';
      case 'Internship':
        return 'bg-purple-100 text-purple-800';
      case 'Freelance':
        return 'bg-pink-100 text-pink-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading job details...</p>
        </div>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-secondary mb-2">Job Not Found</h2>
          <p className="text-gray-600 mb-4">{error || 'The job you\'re looking for doesn\'t exist or has been removed.'}</p>
          <Button onClick={() => navigate('/jobs/view')} variant="outline">
            Browse All Jobs
          </Button>
        </div>
      </div>
    );
  }

  const timeRemaining = getTimeRemaining(job.lastDateToApply);
  const isExpired = timeRemaining.text === 'Expired';

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-surface shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate('/jobs/view')}
              className="text-gray-600 hover:text-secondary"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Jobs
            </Button>
            
            <div className="flex items-center space-x-2">
              <Button onClick={handleShare} variant="outline" size="sm">
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Job Header */}
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-2xl font-bold text-secondary mb-2">
                      {job.title}
                    </CardTitle>
                    <div className="flex items-center space-x-4 text-gray-600 mb-4">
                      <div className="flex items-center space-x-2">
                        <Building className="w-5 h-5" />
                        <span className="font-medium">{job.companyName}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <MapPin className="w-5 h-5" />
                        <span>{job.location}</span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 mb-4">
                      <Badge className={getEmploymentTypeColor(job.employmentType)}>
                        {job.employmentType}
                      </Badge>
                      <Badge variant="secondary">{job.jobCategory}</Badge>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-primary mb-2">
                      {formatSalary(job.salaryRange)}
                    </div>
                    <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${timeRemaining.bg} ${timeRemaining.color}`}>
                      <Clock className="w-4 h-4 mr-1" />
                      {timeRemaining.text}
                    </div>
                  </div>
                </div>
              </CardHeader>
            </Card>

            {/* Job Description */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <BookOpen className="w-5 h-5" />
                  <span>Job Description</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="prose prose-sm max-w-none">
                  <p className="whitespace-pre-wrap text-gray-700 leading-relaxed">
                    {job.description}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Required Skills */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Star className="w-5 h-5" />
                  <span>Required Skills</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {job.requiredSkills.map((skill, index) => (
                    <Badge key={index} variant="outline" className="text-primary border-blue-200">
                      {skill}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Qualifications */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Award className="w-5 h-5" />
                  <span>Qualifications</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="prose prose-sm max-w-none">
                  <p className="whitespace-pre-wrap text-gray-700 leading-relaxed">
                    {job.qualifications}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Apply Card */}
            <Card className="border-blue-200">
              <CardHeader>
                <CardTitle className="text-blue-900">Apply for this Job</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <Button
                    onClick={handleApply}
                    disabled={isExpired || applying}
                    className="w-full bg-primary hover:bg-primary-dark text-white"
                    size="lg"
                  >
                    {isExpired ? 'Application Closed' : 'Apply Now'}
                  </Button>
                  
                  {!isAuthenticated && (
                    <p className="text-sm text-gray-600 text-center">
                      <Button
                        variant="link"
                        onClick={() => navigate('/login')}
                        className="p-0 h-auto text-primary"
                      >
                        Sign in
                      </Button>
                      {' '}to apply for this job
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Job Info */}
            <Card>
              <CardHeader>
                <CardTitle>Job Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2 text-gray-600">
                    <Clock className="w-4 h-4" />
                    <span>Experience Level</span>
                  </div>
                  <Badge variant="secondary">{job.experienceLevel}</Badge>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2 text-gray-600">
                    <Users className="w-4 h-4" />
                    <span>Openings</span>
                  </div>
                  <span className="font-medium">{job.numberOfOpenings}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2 text-gray-600">
                    <Eye className="w-4 h-4" />
                    <span>Views</span>
                  </div>
                  <span className="font-medium">{job.views}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2 text-gray-600">
                    <Calendar className="w-4 h-4" />
                    <span>Posted On</span>
                  </div>
                  <span className="font-medium">{formatDate(job.createdAt)}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2 text-gray-600">
                    <Calendar className="w-4 h-4" />
                    <span>Last Date</span>
                  </div>
                  <span className="font-medium">{formatDate(job.lastDateToApply)}</span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2 text-gray-600">
                    <Briefcase className="w-4 h-4" />
                    <span>Job ID</span>
                  </div>
                  <span className="font-medium font-mono text-sm">{job.jobId}</span>
                </div>
              </CardContent>
            </Card>

            {/* Contact Info */}
            <Card>
              <CardHeader>
                <CardTitle>Contact Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-gray-600" />
                  <div>
                    <p className="text-sm text-gray-600">Email</p>
                    <a
                      href={`mailto:${job.contactEmail}`}
                      className="font-medium text-primary hover:text-blue-700"
                    >
                      {job.contactEmail}
                    </a>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <Phone className="w-5 h-5 text-gray-600" />
                  <div>
                    <p className="text-sm text-gray-600">Phone</p>
                    <a
                      href={`tel:${job.contactPhone}`}
                      className="font-medium text-primary hover:text-blue-700"
                    >
                      {job.contactPhone}
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Posted By */}
            <Card>
              <CardHeader>
                <CardTitle>Posted By</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-gray-600" />
                  </div>
                  <div>
                    <p className="font-medium">
                      {job.postedBy.firstName} {job.postedBy.lastName}
                    </p>
                    <p className="text-sm text-gray-600">{job.companyName}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
} 